<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';

import AddKanban from './AddKanban.vue';
import EditKanban from './EditKanban.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { accountScopedUrl } = useAccount();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedKanban = ref({});

const records = computed(() => getters['kanbans/getKanbans'].value);
const uiFlags = computed(() => getters['kanbans/getUIFlags'].value);

const deleteMessage = computed(() => ` ${selectedKanban.value.name}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedKanban.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedKanban.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteKanban = async id => {
  try {
    await store.dispatch('kanbans/delete', id);
    useAlert(t('KANBAN_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('KANBAN_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedKanban.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedKanban.value.id] = true;
  closeDeletePopup();
  deleteKanban(selectedKanban.value.id);
};

const tableHeaders = computed(() => [
  t('KANBAN_SETTINGS.LIST.TABLE_HEADER.NAME'),
  t('KANBAN_SETTINGS.LIST.TABLE_HEADER.DESCRIPTION'),
  t('KANBAN_SETTINGS.LIST.TABLE_HEADER.STAGES'),
  t('KANBAN_SETTINGS.LIST.TABLE_HEADER.ACTION'),
]);

onBeforeMount(() => {
  store.dispatch('kanbans/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('KANBAN_SETTINGS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="t('KANBAN_SETTINGS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('KANBAN_SETTINGS.HEADER')"
        :description="t('KANBAN_SETTINGS.DESCRIPTION')"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ t('KANBAN_SETTINGS.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="t('KANBAN_SETTINGS.NEW_KANBAN')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="records"
        :no-data-message="t('KANBAN_SETTINGS.LIST.404')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="kanban in items" :key="kanban.id" :item="kanban">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12">
                  {{ kanban.name }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ kanban.description }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ (kanban.kanban_stages || []).length }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <router-link
                    :to="
                      accountScopedUrl(`settings/kanban/${kanban.id}/manage`)
                    "
                  >
                    <Button
                      v-tooltip.top="t('KANBAN_SETTINGS.LIST.MANAGE')"
                      icon="i-lucide-settings"
                      slate
                      sm
                    />
                  </router-link>
                  <Button
                    v-tooltip.top="t('KANBAN_SETTINGS.FORM.EDIT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    :is-loading="loading[kanban.id]"
                    @click="openEditPopup(kanban)"
                  />
                  <Button
                    v-tooltip.top="t('KANBAN_SETTINGS.FORM.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[kanban.id]"
                    @click="openDeletePopup(kanban)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddKanban @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditKanban :selected-response="selectedKanban" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="t('KANBAN_SETTINGS.DELETE.CONFIRM.TITLE')"
      :message="t('KANBAN_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="t('KANBAN_SETTINGS.DELETE.CONFIRM.YES')"
      :reject-text="t('KANBAN_SETTINGS.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
